import {getLocale} from '@epic-core/common';
import {generateRouteTo} from 'epic-fortnite-react';
import {ContextMessage as Message} from '@epic-core/localization';
import {connectToStores} from 'epic-alt-utils';
import {BlogActions} from 'epic-minimalist-blog/app/scripts/actions';
import {GridLayout, LoadIndicator} from 'epic-minimalist-blog/app/scripts/components';
import {BlogStore} from 'epic-minimalist-blog/app/scripts/stores';
import PropTypes from 'prop-types';
import React from 'react';
import {withRouter} from 'react-router';

/**
 * HomeBlog
 * Rendered when the route matches /news or a specified root URL
 * @implements {LoadIndicator}
 * @implements {GridLayout}
 * @implements {FeatureBanner}
 * @implements {Message}
 * @reactProps {object} blogStore
 * @reactProps {object} match
 * @reactProps {bool} showLoading
 * @reactProps {string} routerRootUrl 'news/'
 * @reactProps {string} linkUrlPrefix
 * @reactProps {string} rootPageSlug 'blog'
 * @reactProps {bool} enableTileChunking true
 * @reactProps {boo} enableAutoShortDescription false
 * @reactProps {number} mobilePostNum
 */
@connectToStores
class HomeBlog extends React.Component {
  static getStores() {
    return [BlogStore];
  }

  static getPropsFromStores() {
    return {
      blogStore: BlogStore.getState(),
    };
  }

  static getInitialActions() {
    return [
      (props) => {
        // reset blog store state so it won't read state from news page
        BlogActions.setStoreState.defer({blogList: []});
        BlogActions.getBlogData.defer({
          category: 'all',
          rootPageSlug: HomeBlog.defaultProps.rootPageSlug,
          initCount: props.postsPerPage,
        });
      },
    ];
  }

  static defaultProps = {
    routerRootUrl: 'news',
    rootPageSlug: 'blog',
    enableTileChunking: true,
    enableAutoShortDescription: false,
    hideLoadIndicator: false,
    mobilePostNum: 2,
  };

  state = {
    onLoadMore: this.onLoadMore.bind(this),
    loadOnce: false,
  };

  static propTypes = {
    blogStore: PropTypes.object,
    history: PropTypes.object,
    showLoading: PropTypes.bool,
    // Url used for react-router
    routerRootUrl: PropTypes.string,
    linkUrlPrefix: PropTypes.string,
    // Url used for CMS API back end
    rootPageSlug: PropTypes.string,
    hideLoadIndicator: PropTypes.bool,
    mobilePostNum: PropTypes.number,
    postsPerPage: PropTypes.number,
  };

  onLoadMore() {
    const category = 'all';
    const {rootPageSlug, postsPerPage = 0} = this.props;
    BlogActions.getBlogData({
      category,
      loadMore: true,
      rootPageSlug,
      postsPerPage: 2 * Math.round(Number(postsPerPage) / 2), // find nearest even number to fill grid layout
    });
    this.setState({
      loadOnce: true,
    });
  }

  goToNews = () => {
    const {history, location} = this.props;
    history.push(generateRouteTo('/news', location));
  };

  render() {
    const blogStore = this.props.blogStore || {};
    const locale = getLocale();
    const blogList = blogStore.blogList;
    const initLoading = this.props.showLoading;
    const routerRootUrl = this.props.routerRootUrl;
    const linkUrlPrefix = this.props.linkUrlPrefix || '';
    const mobilePostNum = this.props.mobilePostNum;

    if (initLoading) {
      return (
        <div className="blog-view-container">
          <div className="blog-view">{!this.props.hideLoadIndicator ? <LoadIndicator /> : ''}</div>
        </div>
      );
    }

    let loadMoreButton;
    const loadingStyle = {display: blogStore && blogStore.loadingArticles ? 'block' : 'none'};
    /**
     * Loading states and 'Load More' buttons
     */

    if (blogStore.hasMoreArticles) {
      loadMoreButton = (
        <div className="load-more">
          <button className="btn btn-primary btn-load" onClick={this.state.onLoadMore}>
            <Message code="epic.blog.load_more" />
          </button>
        </div>
      );
    } else {
      loadMoreButton = '';
    }

    const blogs = (
      <GridLayout
        blogs={blogList}
        currentCategory={blogStore.category}
        locale={locale}
        enableSlider={true}
        mobilePostNum={mobilePostNum}
        routerRootUrl={routerRootUrl}
        linkUrlPrefix={linkUrlPrefix}
      />
    );

    return (
      <div className="blog-wrapper">
        <div className="blog-view-container">
          <div className="blog-view">
            <div>
              {blogs}
              <div className="loading-icon blog-loading" style={loadingStyle} />
              {this.state.loadOnce ? (
                <div className="load-more">
                  <button className="btn btn-primary btn-load" onClick={this.goToNews}>
                    <Message code="epic.fortnite.blog.all_news" />
                  </button>
                </div>
              ) : (
                loadMoreButton
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(HomeBlog);
